import CustomLoading from '@/components/custom-loading';
import { Card, CardContent, CardTitle } from '@/components/ui/card';
import {
  calculatePercentChange,
  formatNumber,
  formatNumberToCurrency,
} from '@/lib/utils';
import { TTotalSold } from '@/types/gift-card-transaction/transaction.type';
import { dateToPreviousDate } from '@/utils/constant';
import clsx from 'clsx';
import { Minus, TrendingDown, TrendingUp } from 'lucide-react';
import { TZDate } from 'react-day-picker';

const CardItem = (props: {
  title: string;
  data: string | undefined;
  isLoading: boolean;
  percentage: number;
  type: string;
  className?: string;
  classNameType?: string;
}) => {
  return (
    <Card
      className={clsx(
        'rounded-xl border bg-dark-blue text-white',
        props.className,
      )}
    >
      <CardTitle className="px-6 pb-12 pt-6 text-2xl font-semibold">
        {props.title}
      </CardTitle>

      <CardContent>
        {props.isLoading ? (
          <CustomLoading className="!min-h-[84px]" />
        ) : (
          <>
            <div className="text-5xl font-bold">{props.data}</div>
            {props.percentage === 0 ? (
              <p className={'flex items-end gap-1 pt-2 text-xl font-semibold'}>
                <Minus />
                {formatNumber(props.percentage)}%
                <span className={props.classNameType}>from {props.type}</span>
              </p>
            ) : (
              <p
                className={clsx(
                  props.percentage > 0
                    ? 'text-success'
                    : 'text-danger-discount',
                  'flex items-center gap-2 pt-2 text-xl font-semibold',
                )}
              >
                {props.percentage > 0 ? <TrendingUp /> : <TrendingDown />}
                {props.percentage > 0
                  ? `+${formatNumber(props.percentage)}`
                  : formatNumber(props.percentage)}
                %<span className={props.classNameType}>from {props.type}</span>
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};
const TransactionAggregate = (props: {
  startDate: TZDate;
  endDate: TZDate;
  whmcsId: number | undefined;
  totalGiftCardSold: TTotalSold | undefined;
  totalPreviousGiftCardSold: TTotalSold | undefined;
  totalRedeem: TTotalSold | undefined;
  totalPreviousRedeem: TTotalSold | undefined;
  totalAddFund: TTotalSold | undefined;
  totalPreviousAddFund: TTotalSold | undefined;
}) => {
  const dateTimeFilter = dateToPreviousDate(props.startDate, props.endDate);

  const soldPercentChange = calculatePercentChange(
    props.totalGiftCardSold?.data ?? 0,
    props.totalPreviousGiftCardSold?.data ?? 0,
  );

  const redeemPercentChange = calculatePercentChange(
    props.totalRedeem?.data ?? 0,
    props.totalPreviousRedeem?.data ?? 0,
  );

  const addFundPercentChange = calculatePercentChange(
    props.totalAddFund?.data ?? 0,
    props.totalPreviousAddFund?.data ?? 0,
  );

  return (
    <div className="grid grid-cols-3 gap-4 px-6">
      <CardItem
        title={'Total sell & add funds'}
        data={formatNumberToCurrency(props.totalAddFund?.data ?? 0)}
        isLoading={props.totalAddFund?.isLoading ?? false}
        percentage={addFundPercentChange}
        type={dateTimeFilter.label}
        classNameType="text-gray-white"
      />
      <CardItem
        title={'Total cards sold'}
        data={props.totalGiftCardSold?.data?.toString() ?? '0'}
        isLoading={props.totalGiftCardSold?.isLoading ?? false}
        percentage={soldPercentChange}
        type={dateTimeFilter.label}
        className="border-gray-border bg-gray-white text-gray-primary"
        classNameType="text-gray-primary"
      />

      <CardItem
        title={'Redeemed'}
        data={formatNumberToCurrency(props.totalRedeem?.data ?? 0)}
        isLoading={props.totalRedeem?.isLoading ?? false}
        percentage={redeemPercentChange}
        type={dateTimeFilter.label}
        className="border-gray-border bg-gray-white text-gray-primary"
        classNameType="text-gray-primary"
      />
    </div>
  );
};

export default TransactionAggregate;
